import ApiService from "../../../core/service/api-service";
import { useTranslation } from "react-i18next";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useRowDetailDrawer } from "../../../core/components/data-grid/useRowDetailDrawer";
import { useEffect, useMemo, useRef, useState } from "react";
import { DataGridProvider, useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import studyGridColumns from "./StudyColumns";
import StudyGridTools from "./StudyGridTools";

import './row-styles.css'
import StudyAddRequest from "../study-add-request/StudyAddRequest";
import { useModal } from "../../../core/context/ModalContext";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import Colors from "../../../core/style/Colors";
import { useSignalR } from "../../../core/context/SignalRContext";
import useFocusListener from "../../../core/components/FocusListener";
import { Button, Modal, Tabs, Tooltip, Typography } from 'antd'
import { useApp } from "../../../core/context/AppContext";
import { EyeOutlined, AlertOutlined, ExpandOutlined, FilePdfOutlined } from "@ant-design/icons";
import { BsFillMicFill } from "react-icons/bs";
import { HiOutlineDocumentText } from 'react-icons/hi';
import { BsFileArrowDownFill } from 'react-icons/bs';
import Flex from "../../../core/components/Flex";
import EnumHelper from "../../../enums/EnumHelper";
import StudyRequestGrid from "../../request/request-data-grid/StudyRequestGrid";
import VoiceRecordGrid from "../../voice-record/voice-record-grid/VoiceRecordGrid";
import StudyFileGrid from "../study-files/grid/StudyFileGrid";
import StudyTypeEnum from "../../../enums/StudyTypeEnum";
import StudyStatusEnum from "../../../enums/StudyStatusEnum";
import StudyHelper from "../StudyHelper";
import ReportModal from "../../../pages/report/ReportModal";
import { dateFormat, getAge } from "../../../core/utils/date-utils";
import { normalizeStudyFilter } from "./study-filter-query";
import StudyLogsGrid from "../StudyLogsGrid";
import StudyTasksGrid from "../StudyTasksGrid";
import SeriesMetadataTable from "../SeriesMetadataTable";
import StudyRowMenu from "./StudyRowMenu";
import InstitutionCell from "./InstitutionCell";
import StudyAutoExportHistoryGrid from "../study-auto-export-history/StudyAutoExportHistoryGrid";
import StudyNotesPanel from "../StudyNotesPanel";
import StudyReportsModal from "../StudyReportsModal";

export default function StudyGrid({ }) {
    const { t } = useTranslation();
    const { setDataGridProps, queryModel, setData, setPaginationResult, setRowColor, selectedRows, setSelectedRows, refreshDataGrid, onFocusOut, tableId, loadCount, setLoading } = useDataGrid()
    const { openModal, closeModal, modal } = useModal()
    const { user, hasPermission } = useUser()
    const { onUpdateStudyGrid, hubContext } = useSignalR()
    const { addKeyEvent, removeKeyEvent, pressedKey } = useApp()
    const isFocus = useFocusListener({ elementId: tableId })

    /**
     * background: SignalR'dan gelen tazeleme. Kullanıcı bir şey istemedi — ne tam ekran karartma
     * ne de tablo spinner'ı çıkar; liste sessizce güncellenir. Kullanıcının başlattığı yüklemede
     * (ilk açılış, filtre, sayfa, sıralama, yenile) gösterge TABLONUN İÇİNDE gösterilir.
     */
    const initDataGrid = async ({ background = false } = {}) => {
        if (!background)
            setLoading(true)

        const _qm = {
            ...queryModel
        }

        let sortItems = queryModel.orderByItems
        if (_qm.filter.studyTypeList && _qm.filter.studyTypeList.find(x => x === StudyTypeEnum.EMERGENCY)) {
            if (queryModel.orderByItems?.length === 0) {
                sortItems = [
                    {
                        name: 'studyDetail.timeoutDate', operation: 2
                    }
                ]
            }
        }
        _qm.orderByItems = sortItems;
        // Filtre normalizasyonu ortak modülde (bkz. study-filter-query): filtreyi sunucuya taşıyan
        // her yol aynı fonksiyondan geçmeli.
        _qm.filter = normalizeStudyFilter(_qm.filter);
        // silent: küresel gösterge susar; geri bildirim tablonun kendi loading'inde.
        try {
            const response = await ApiService.Study.DataGrid(_qm, { silent: true });

            setData(response.data)
            setPaginationResult(response.pagination)

            // Sayaç ayrı istek, sayfa çizildikten SONRA. Aynı filtreyle sayfa değiştirmek yeniden saymaz
            // (anahtar filtrenin kendisi); milyonlarca satırda COUNT sayfa sorgusundan pahalıdır.
            loadCount((exact) => ApiService.Study.DataGridCount(_qm, exact), JSON.stringify(_qm.filter))

            // Keep the keyboard-selected study in sync with the fresh data so shortcuts
            // (e.g. the R report key checking isRead) don't act on a stale record after
            // a set-read or any other refresh.
            if (selectedStudy.current?.id && Array.isArray(response.data)) {
                const fresh = response.data.find(x => x.id === selectedStudy.current.id);
                if (fresh) {
                    selectedStudy.current = fresh;
                    forceRender(x => x + 1);
                }
            }
        }
        finally {
            if (!background)
                setLoading(false)
        }
    }

    const selectedStudy = useRef(null)
    const selectedReq = useRef(null)
    // The study currently expanded in the right drawer (kept in a ref so the
    // keyboard-shortcut effect always sees the latest without re-subscribing).
    const drawerStudyRef = useRef(null)
    // Whether any shared-context modal (Report, Report Preview, Request & Reports,
    // Report Versions, …) is currently open. Kept in a ref so the keyboard-shortcut
    // effect sees the latest without re-subscribing / re-firing on modal changes.
    const modalOpenRef = useRef(false)
    // Seçim değişince grid'i yeniden render edip rowClassName'i (selected-row) tazelemek için.
    const [, forceRender] = useState(0)

    useEffect(() => {
        console.log('[StudyGrid] useEffect 1: onUpdateStudyGrid changed', { hasPermission: hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER), onUpdateStudyGridKey: onUpdateStudyGrid?.key });
        if (hasPermission(PERMISSIONS.HOSPITAL_GROUP_FILTER) && onUpdateStudyGrid?.key) {
            // SignalR tetikledi: kullanıcı istemedi → sessiz tazeleme (gösterge yok).
            initDataGrid({ background: true })
        }
    }, [onUpdateStudyGrid])

    useEffect(() => {
        // console.log('[StudyGrid] useEffect 2: addKeyEvent');
        addKeyEvent([
            { keyCode: 'KeyR', modifier: null, prevent: false, },
            { keyCode: 'KeyA', modifier: null, prevent: false, },
        ])
    }, []);

    useEffect(() => {
        // console.log('[StudyGrid] useEffect 3: pressedKey changed', { isFocus, pressedKey, selectedStudyId: selectedStudy.current?.id });
        // A modal is already open (ours, or one opened from a row menu such as
        // "Request & Reports"/"Report Preview" inside the expanded drawer) → don't
        // stack another. Reading the ref covers every modal opened via ModalContext,
        // not just the two we open with hard-coded ids.
        if (modalOpenRef.current)
            return

        // Don't hijack typing.
        const element = document.activeElement;
        if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable))
            return

        // A study expanded in the right drawer takes priority; otherwise the row
        // selected in the grid (only while the grid itself is focused).
        const drawerStudy = drawerStudyRef.current;
        const target = drawerStudy || (isFocus ? selectedStudy.current : null);
        if (!target?.id)
            return
        const req = drawerStudy ? null : selectedReq.current;

        if (!pressedKey?.modifier?.code) {
            switch (pressedKey?.key?.code) {
                case 'KeyA': {
                    addRequestFor(target)
                    break
                }
                case 'KeyR': {
                    if (!hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT))
                        break
                    openReportFor(target, req)
                }
            }
        }
    }, [pressedKey]);

    const addRequestFor = (study) => {
        if (!study?.id) return
        openModal(t('Add Request & Anamnez'), (
            <div id={'study-add-request-modal'} style={{ display: 'contents' }}>
                <StudyAddRequest
                    studyRow={study}
                    onSaved={async () => {
                        refreshDataGrid()
                        refreshDetail()
                        closeModal()
                    }}
                    studyId={study.id}
                />
            </div>
        ))
    }

    const openReportFor = (study, req = null) => {
        if (!study?.id) return
        if (StudyHelper.openReqRepPage(req, study)) {
            openModal(t('Report'), (
                <div id={'study-report-modal'} style={{ display: 'contents' }}>
                    <ReportModal
                        studyId={study.id}
                        requestId={req?.id}
                    />
                </div>
            ), () => {
                // Modalda yazılan/onaylanan rapor study durumunu (dolayısıyla satır rengini)
                // değiştirir; modal kendi grid'ini tazeler ama dıştaki grid ve açık detay
                // kartı bayat kalır. Kapanışta ikisini de tazele.
                refreshDataGrid()
                refreshDetail()
            }, window.innerWidth * 0.98, 20)
        }
    }


    useEffect(() => {
        if (window.studyGridKey !== queryModel.updateKey) {
            window.studyGridKey = queryModel.updateKey
            initDataGrid()
        }
    }, [queryModel.updateKey])

    // The study columns, built once. Used both for the grid and to render the
    // full row info inside each detail card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const detailColumns = useMemo(() => studyGridColumns(refreshDataGrid, hasPermission), [])

    useEffect(() => {
        setDataGridProps({
            rowKey: 'id',
            columns: [expandColumn, ...detailColumns],
            enableRowSelection: true,
            virtual: true,
            // Mobile card "Details" button opens the same info + tabs (on desktop
            // the grid is virtual, so antd ignores expandable and the drawer is used).
            rowExpandable: () => true,
            expandedRowRender: (record) => (
                <div style={{ background: '#fff', padding: 8 }}>
                    {renderStudyInfo(record)}
                    {renderStudyDetail(record)}
                </div>
            ),
            rowClassName: (record, index) => {
                let className = 'normal-row';
                const isSelected = selectedStudy.current?.id === record.id;

                if(!record.isReportable && !record.isDeleted) {
                    className = 'no-report-row'
                    return isSelected ? className + ' selected-row' : className;
                }

                if (record.isDeleted)
                    className = 'deleted-row ';
                else if (record.studyType === StudyTypeEnum.EMERGENCY) {
                    if (record.studyStatus === StudyStatusEnum.COMPLETED)
                        className = 'has-report-row';
                    else if (record.studyStatus === StudyStatusEnum.TIMEOUT_HAS_NOT_CONFIRMED_REPORT)
                        className = 'has-report-row';
                    else
                        className = 'emergency-row';
                }
                else {
                    if (record.studyStatus === StudyStatusEnum.WAITING) {
                        className = 'no-report-row'
                    }
                    else if (record.studyStatus === StudyStatusEnum.HAS_NOT_CONFIRMED_REPORT) {
                        className = 'not-confirmed-row';
                    }
                    else if (record.studyStatus === StudyStatusEnum.TIMEOUT_HAS_NOT_CONFIRMED_REPORT) {
                        className = 'has-report-row';
                    }
                    else if (record.studyStatus === StudyStatusEnum.COMPLETED) {
                        className = 'has-report-row';
                    }
                }

                return isSelected ? className + ' selected-row' : className;
            },
            onRow: (record) => {
                return {
                    onClick: (e) => {
                        selectStudyRow(record)
                    },
                    onDoubleClick: () => {

                    }
                }
            }
        })
    }, [])

    const selectStudyRow = (record) => {
        if (selectedStudy?.current?.id === record?.id) {
            selectedStudy.current = null;
            selectedReq.current = null;
            forceRender(x => x + 1);
            return
        }
        selectedStudy.current = record
        selectedReq.current = null
        forceRender(x => x + 1);
    }

    const renderStudyDetail = (record) => (
        <Tabs defaultActiveKey="1" size={'small'} style={{ backgroundColor: '#fff' }}>
                {
                    hasPermission(PERMISSIONS.STUDY_REQUEST_LIST) && (
                        <Tabs.TabPane tab={t('Requests')} key="1">
                            <DataGridProvider name={'study-request-provider'} defaultFilter={{}} onRefresh={refreshDataGrid}>
                                <StudyRequestGrid
                                    studyId={record.id}
                                    onSelected={(req) => {
                                        if (record.id !== selectedStudy?.current?.id)
                                            selectStudyRow(record)
                                        selectedReq.current = req
                                    }}
                                />
                            </DataGridProvider>
                        </Tabs.TabPane>
                    )
                }
                {
                    hasPermission(PERMISSIONS.VOICE_RECORD_LIST) && (
                        <Tabs.TabPane tab={t('Voice Records')} key="2">
                            <DataGridProvider name={'study-voice-rec-provider'} defaultFilter={{}} onRefresh={refreshDataGrid}>
                                <VoiceRecordGrid studyId={record.id} />
                            </DataGridProvider>
                        </Tabs.TabPane>
                    )
                }
                {
                    hasPermission(PERMISSIONS.STUDY_FILE_LIST) && (
                        <Tabs.TabPane tab={t('Files')} key="3">
                            <DataGridProvider name={'study-file-provider'} onRefresh={refreshDataGrid}>
                                <StudyFileGrid studyId={record.id} />
                            </DataGridProvider>
                        </Tabs.TabPane>
                    )
                }
                <Tabs.TabPane tab={t('Dicom Data')} key="dicom">
                    <SeriesMetadataTable studyId={record.id} studyInstanceUid={record.studyInstanceUid} />
                </Tabs.TabPane>
                {
                    hasPermission(PERMISSIONS.STUDY_LOG) && (
                        <Tabs.TabPane tab={t('Study Logs')} key="4">
                            <DataGridProvider>
                                <StudyLogsGrid studyId={record.id} />
                            </DataGridProvider>
                        </Tabs.TabPane>
                    )
                }
                {
                    hasPermission(PERMISSIONS.STUDY_TASK_MANAGER) && (
                        <Tabs.TabPane tab={t('Tasks')} key="5">
                            <DataGridProvider>
                                <StudyTasksGrid studyId={record.id} />
                            </DataGridProvider>
                        </Tabs.TabPane>
                    )
                }
                {
                    hasPermission(PERMISSIONS.AUTO_EXPORT_MANAGE) && (
                        <Tabs.TabPane tab={t('Auto Export History')} key="6">
                            <DataGridProvider name={'study-auto-export-history-provider'} defaultFilter={{}}>
                                <StudyAutoExportHistoryGrid studyId={record.id} />
                            </DataGridProvider>
                        </Tabs.TabPane>
                    )
                }
                {/* Notlar: ayrı bir izin yok — çekimi görebilen not yazabilir/okuyabilir (§2.4 sunucuda eler). */}
                <Tabs.TabPane tab={t('Notes')} key="7">
                    <StudyNotesPanel studyId={record.id} />
                </Tabs.TabPane>
            </Tabs>
    )

    // Compact, label-less patient/study summary shown at the top of each drawer
    // card (replaces the verbose Descriptions table).
    const renderStudyInfo = (record) => (
        <div style={{ fontSize: 12, lineHeight: 1.6, marginBottom: 4 }}>
            <div style={{ fontWeight: 600 }}>
                {record.patientName || '*'} ({getAge(record.birthdate)}/{record.patientSex || '*'})
            </div>
            <div>
                {record.accessionNumber || '*'} / {record.studyDate ? dateFormat(record.studyDate) : '*'} / {t(EnumHelper.getKeyByValue(StudyTypeEnum, record.studyType))}
            </div>
            <div style={{ color: '#666' }}>{record.studyDescription || '*'}</div>
        </div>
    )

    const { expandColumn, drawer, refreshDetail, openItems, activeKeys } = useRowDetailDrawer({
        name: 'study',
        getTitle: (record) => (
            `${record.patientName || '-'} · ${record.accessionNumber || record.id}`
            + (record.modality ? ` · ${record.modality}` : '')
        ),
        renderInfo: renderStudyInfo,
        renderDetail: renderStudyDetail,
    })

    // Keep the shortcut target (drawer study) fresh: the currently-expanded panel,
    // else the last opened card.
    drawerStudyRef.current = openItems.length
        ? (openItems.find(r => String(r.id) === String(activeKeys[activeKeys.length - 1])) || openItems[openItems.length - 1])
        : null;

    // Mirror the current modal-open state so the pressedKey effect can bail out
    // while any context modal (incl. those opened from row menus) is showing.
    modalOpenRef.current = !!(modal?.open || modal?.modal2?.open);

    useEffect(() => {
        console.log('[StudyGrid] useEffect 6: onFocusOut changed', { selectedStudyId: selectedStudy.current?.id });
        if (selectedStudy.current) {
            selectedStudy.current = null
            forceRender(x => x + 1);
        }
    }, [onFocusOut])

    return (
        <>
            <DataGrid
                title={() => (
                    <StudyGridTools />
                )}
                name={'study'}
                // inDrawer: kart, detay çekmecesinin başlığı olarak çiziliyor. Orada yalnız işlem
                // satırı kalır — hasta adı, açıklama, kurum ve hasta kimliği hemen altındaki
                // detay panelinde zaten var, başlıkta tekrar etmeleri yer kaplıyordu.
                mobileCardTitle={(record, { canOpenDetails, openDetails, inDrawer } = {}) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        {!inDrawer && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{record.patientName}</span>
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                <span style={{
                                    backgroundColor: record.studyType === StudyTypeEnum.EMERGENCY ? '#ff4d4f' : '#1890ff',
                                    color: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    fontWeight: 500
                                }}>
                                    {t(EnumHelper.getKeyByValue(StudyTypeEnum, record.studyType))}
                                </span>
                                <span style={{
                                    backgroundColor: '#1890ff',
                                    color: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    fontWeight: 500
                                }}>
                                    {record.modality}
                                </span>
                            </div>
                        </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#666', alignItems: 'center' }}>
                            <StudyRowMenu row={record} />
                            {canOpenDetails && (
                                <Button
                                    type={'primary'}
                                    size={'small'}
                                    icon={<ExpandOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openDetails();
                                    }}
                                >
                                    {t('Details')}
                                </Button>
                            )}
                            <span style={{
                                backgroundColor: '#52c41a',
                                color: '#fff',
                                padding: '1px 6px',
                                borderRadius: 4,
                                fontSize: 11
                            }}>
                                {record.reportCount}/{record.requestCount}
                            </span>
                            <Flex alignItems={'center'} style={{ gap: 4 }}>
                                {record.isRead && (
                                    <Tooltip title={t('Read')}>
                                        <EyeOutlined style={{ color: '#2980b9' }} />
                                    </Tooltip>
                                )}
                                {record.isEmergency && (
                                    <Tooltip title={t('Emergency')}>
                                        <AlertOutlined style={{ color: '#c0392b' }} />
                                    </Tooltip>
                                )}
                                {record.hasVoiceRecord && (
                                    <Tooltip title={t('Voice Record')}>
                                        <BsFillMicFill />
                                    </Tooltip>
                                )}
                                {record.hasAnamnez && (
                                    <Tooltip title={t('Anamnesis')}>
                                        <HiOutlineDocumentText />
                                    </Tooltip>
                                )}
                                {record.hasFile && (
                                    <Tooltip title={t('File')}>
                                        <BsFileArrowDownFill color={'#3742fa'} />
                                    </Tooltip>
                                )}
                            </Flex>
                        </div>
                        {!inDrawer && (
                        <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#888' }}>
                            <span>{t('Patient ID')}: {record.dicomPatientId}</span>
                            <span>{t('Accession Number')}: {record.accessionNumber}</span>
                        </div>
                        )}
                        {/* Açıklama uzun ve serbest metin: karta sığmayanı kesip tam hâlini tooltip'e ver. */}
                        {!inDrawer && (
                        <Tooltip title={record.studyDescription || '-'}>
                            <Typography.Text ellipsis style={{ fontSize: 12, color: '#888' }}>
                                {record.studyDescription || '-'}
                            </Typography.Text>
                        </Tooltip>
                        )}
                        {/* Kurum + Raporlar aynı satırda yarı yarıya. Rapor düğmesi yoksa kurum satırı
                            tek başına doldurur; yarım boş satır bırakmanın anlamı yok. */}
                        {!inDrawer && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                <InstitutionCell
                                    record={record}
                                    canEdit={hasPermission(PERMISSIONS.EDIT_STUDY_INSTITUTION)}
                                    onChanged={refreshDataGrid}
                                    label={t('Institution')}
                                    inBody
                                />
                            </div>
                            {/* İki izin de şart: liste STUDY_REQUEST_LIST ile geliyor, PDF
                                REPORT_DOWNLOAD_PDF ile üretiliyor (sunucuda ikisi de kontrol ediliyor). */}
                            {hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF)
                                && hasPermission(PERMISSIONS.STUDY_REQUEST_LIST)
                                && record.confirmedReportCount > 0 && (
                                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <Button
                                        size={'small'}
                                        icon={<FilePdfOutlined />}
                                        style={{ width: '100%' }}
                                        onClick={(e) => {
                                            // Kart tıklaması detay çekmecesini açıyor; düğme kendi işini yapsın.
                                            e.stopPropagation();
                                            openModal(
                                                t('Reports'),
                                                <StudyReportsModal studyId={record.id} />
                                            );
                                        }}
                                    >
                                        {t('Reports')} ({record.confirmedReportCount})
                                    </Button>
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                )}
            />

            {drawer}
        </>
    )
}
